/**
 * Deck Validation Engine
 *
 * Validates decks against format rules and provides detailed feedback.
 * Supports user overrides for conscious rule breaking.
 */
import { canPairAsCommanders, isBackgroundCard } from '@/lib/commanderPartners';
import { DECK_ZONE_LABELS, FORMAT_RULES, isBasicLand, isCommanderFormat } from '@/lib/deckUtils';
import type { Color, ScryfallCard } from '@/types/card';
import type { Deck, DeckFormat, DeckStatus, DeckValidation, DeckZone, EnrichedDeckCard, FormatRules, ValidationIssue } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

export interface DeckValidationInput {
  deck: Deck;
  cards: EnrichedDeckCard[];
  allowedIllegalCards?: string[]; // Scryfall IDs user has overridden
}

export interface CardLegalityInfo {
  isLegal: boolean;
  isBanned: boolean;
  isRestricted: boolean;
  reason?: string;
}

// ============================================================================
// Main Validation Function
// ============================================================================

/**
 * Validates a deck against its format rules.
 * Returns validation result with errors, warnings, and override info.
 */
export function validateDeck(input: DeckValidationInput): DeckValidation {
  const { deck, cards, allowedIllegalCards = [] } = input;
  const allowedSet = new Set(allowedIllegalCards);

  const issues: ValidationIssue[] = [];

  // Get format rules (default to casual if no format)
  const rules = deck.format ? FORMAT_RULES[deck.format] : FORMAT_RULES.casual;

  // Only validate if deck wants legality enforcement
  if (deck.enforce_legality && deck.format) {
    // 1. Deck size validation
    issues.push(...validateDeckSize(cards, rules));

    // 2. Sideboard size validation
    issues.push(...validateSideboardSize(cards, rules));

    // 3. Card copy limits
    issues.push(...validateCardCopies(cards, rules, allowedSet));

    // 4. Commander requirements
    if (rules.requires_commander) {
      issues.push(...validateCommander(cards, deck.format, allowedSet));
    }

    // 5. Color identity (for commander/brawl)
    if (rules.check_color_identity) {
      issues.push(...validateColorIdentity(cards, allowedSet));
    }

    // 6. Format legality (banned/not legal cards)
    issues.push(...validateFormatLegality(cards, deck.format, allowedSet));

    // 7. Rarity restriction (pauper)
    if (rules.rarity_restriction) {
      issues.push(...validateRarityRestriction(cards, rules.rarity_restriction, allowedSet));
    }
  }

  // 8. Zone assignment validation (always check - tokens must be in tokens zone)
  issues.push(...validateZoneAssignments(cards));

  // 9. Warnings (always check, even for casual)
  issues.push(...generateWarnings(cards, deck.format));

  // Separate errors from warnings
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');

  // Check override status
  const hasOverrides = issues.some((i) => i.is_overridden);

  return {
    is_valid: errors.length === 0 || errors.every((e) => e.is_overridden),
    has_overrides: hasOverrides,
    errors,
    warnings,
  };
}

// ============================================================================
// Validation Helpers
// ============================================================================

function validateDeckSize(cards: EnrichedDeckCard[], rules: FormatRules): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Count cards in main zones (commander, companion, main)
  const mainZones: DeckZone[] = ['commander', 'companion', 'main'];
  const mainCount = cards.filter((c) => mainZones.includes(c.zone)).reduce((sum, c) => sum + c.quantity, 0);

  // Minimum deck size
  if (rules.min_deck_size > 0 && mainCount < rules.min_deck_size) {
    issues.push({
      type: 'deck_size_min',
      severity: 'error',
      message: `Deck needs at least ${rules.min_deck_size} cards`,
      suggested_action: `Add ${rules.min_deck_size - mainCount} more cards`,
      can_override: false,
      is_overridden: false,
    });
  }

  // Maximum deck size (usually only for singleton formats)
  if (rules.max_deck_size !== null && mainCount > rules.max_deck_size) {
    issues.push({
      type: 'deck_size_max',
      severity: 'error',
      message: `Deck has ${mainCount} cards but maximum is ${rules.max_deck_size}`,
      suggested_action: `Remove ${mainCount - rules.max_deck_size} cards`,
      can_override: false,
      is_overridden: false,
    });
  }

  return issues;
}

function validateSideboardSize(cards: EnrichedDeckCard[], rules: FormatRules): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  if (!rules.has_sideboard) return issues;

  const sideboardCount = cards.filter((c) => c.zone === 'sideboard').reduce((sum, c) => sum + c.quantity, 0);

  if (sideboardCount > rules.max_sideboard) {
    issues.push({
      type: 'sideboard_size',
      severity: 'error',
      message: `Sideboard has ${sideboardCount} cards but maximum is ${rules.max_sideboard}`,
      suggested_action: `Remove ${sideboardCount - rules.max_sideboard} cards from sideboard`,
      can_override: false,
      is_overridden: false,
    });
  }

  return issues;
}

function validateCardCopies(cards: EnrichedDeckCard[], rules: FormatRules, allowedSet: Set<string>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Skip for limited formats (no copy restrictions)
  if (rules.max_copies >= 999) return issues;

  // Group by card name (not by scryfall_id, as different printings count together)
  const countByName = new Map<string, { count: number; cards: EnrichedDeckCard[] }>();

  for (const card of cards) {
    // Skip maybeboard and tokens
    if (card.zone === 'maybeboard' || card.zone === 'tokens') continue;

    const name = card.card.name;
    const existing = countByName.get(name) ?? { count: 0, cards: [] };
    existing.count += card.quantity;
    existing.cards.push(card);
    countByName.set(name, existing);
  }

  for (const [name, { count, cards: cardInstances }] of countByName) {
    // Basic lands are always unlimited
    if (isBasicLand(name)) continue;

    // Check if exceeds max copies
    if (count > rules.max_copies) {
      const firstCard = cardInstances[0];
      const isOverridden = allowedSet.has(firstCard.scryfall_id);

      issues.push({
        type: 'card_copies',
        severity: 'error',
        message: `${name} has ${count} copies but maximum is ${rules.max_copies}`,
        card_id: firstCard.scryfall_id,
        card_name: name,
        suggested_action: rules.max_copies === 1 ? `Remove ${count - 1} copies (singleton format)` : `Reduce to ${rules.max_copies} copies`,
        can_override: true,
        is_overridden: isOverridden,
      });
    }
  }

  return issues;
}

function validateCommander(cards: EnrichedDeckCard[], format: DeckFormat, allowedSet: Set<string>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  const commanders = cards.filter((c) => c.zone === 'commander');

  if (commanders.length === 0) {
    issues.push({
      type: 'commander_invalid',
      severity: 'error',
      message: 'Deck requires a commander',
      suggested_action: 'Add a legendary creature to the commander zone',
      can_override: false,
      is_overridden: false,
    });
    return issues;
  }

  // Validate each commander
  for (const commander of commanders) {
    const card = commander.card;

    if (!canBeCommander(card, format)) {
      const isOverridden = allowedSet.has(commander.scryfall_id);
      issues.push({
        type: 'commander_invalid',
        severity: 'error',
        message: `${card.name} cannot be a commander (must be a legendary creature, a Background, or have "can be your commander")`,
        card_id: commander.scryfall_id,
        card_name: card.name,
        suggested_action: 'Choose a legendary creature as commander',
        can_override: true,
        is_overridden: isOverridden,
      });
    }
  }

  // Format-specific commander rules
  if (format === 'commander') {
    // EDH allows 1 commander, or 2 that form a legal partnership
    // (Partner, Partner with, Friends forever, Doctor's companion, Choose a Background).
    if (commanders.length > 2) {
      issues.push({
        type: 'commander_invalid',
        severity: 'error',
        message: 'Too many commanders (maximum 2 with a partner ability)',
        suggested_action: 'Remove extra commanders',
        can_override: true,
        is_overridden: false,
      });
    } else if (commanders.length === 2) {
      const [first, second] = commanders;
      if (!canPairAsCommanders(first.card, second.card)) {
        issues.push({
          type: 'commander_invalid',
          severity: 'error',
          message: `${first.card.name} and ${second.card.name} cannot share the command zone`,
          suggested_action:
            'Two commanders need Partner, Friends forever, a matching "Partner with", or "Choose a Background" plus a Background',
          can_override: true,
          is_overridden: false,
        });
      }
    } else if (isBackgroundCard(commanders[0].card)) {
      // A Background is only ever a second commander.
      const background = commanders[0];
      issues.push({
        type: 'commander_invalid',
        severity: 'error',
        message: `${background.card.name} is a Background and cannot be your only commander`,
        card_id: background.scryfall_id,
        card_name: background.card.name,
        suggested_action: 'Add a commander with "Choose a Background"',
        can_override: true,
        is_overridden: allowedSet.has(background.scryfall_id),
      });
    }
  } else if (format === 'brawl') {
    // Brawl allows 1 commander (planeswalker or legendary creature)
    if (commanders.length > 1) {
      issues.push({
        type: 'commander_invalid',
        severity: 'error',
        message: 'Brawl only allows 1 commander',
        suggested_action: 'Remove extra commanders',
        can_override: false,
        is_overridden: false,
      });
    }
  } else if (format === 'oathbreaker') {
    // Oathbreaker: 1 planeswalker commander + 1 signature spell
    const planeswalkers = commanders.filter((c) => c.classification.is_planeswalker);
    if (planeswalkers.length !== 1) {
      issues.push({
        type: 'commander_invalid',
        severity: 'error',
        message: 'Oathbreaker requires exactly 1 planeswalker as commander',
        suggested_action: 'Set 1 planeswalker as commander',
        can_override: false,
        is_overridden: false,
      });
    }
  }

  return issues;
}

function validateColorIdentity(cards: EnrichedDeckCard[], allowedSet: Set<string>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Get commander color identity
  const commanders = cards.filter((c) => c.zone === 'commander');
  if (commanders.length === 0) return issues;

  const commanderIdentity = new Set<Color>();
  for (const commander of commanders) {
    const colors = commander.card.color_identity ?? [];
    colors.forEach((c) => commanderIdentity.add(c));
  }

  // Check each card
  for (const card of cards) {
    if (card.zone === 'commander' || card.zone === 'maybeboard' || card.zone === 'tokens') continue;

    const cardColors = card.card.color_identity ?? [];
    const outsideColors = cardColors.filter((c) => !commanderIdentity.has(c));

    if (outsideColors.length > 0) {
      const isOverridden = allowedSet.has(card.scryfall_id);
      issues.push({
        type: 'color_identity',
        severity: 'error',
        message: `${card.card.name} has color identity outside commander's (${outsideColors.join(', ')})`,
        card_id: card.scryfall_id,
        card_name: card.card.name,
        suggested_action: 'Remove card or change commander',
        can_override: true,
        is_overridden: isOverridden,
      });
    }
  }

  return issues;
}

function validateFormatLegality(cards: EnrichedDeckCard[], format: DeckFormat, allowedSet: Set<string>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Skip for limited formats (all drafted/opened cards are legal)
  const rules = FORMAT_RULES[format];
  if (rules?.is_limited) return issues;

  // Skip for casual (no legality restrictions, and Scryfall has no 'casual' legality field)
  if (format === 'casual') return issues;

  for (const card of cards) {
    if (card.zone === 'maybeboard' || card.zone === 'tokens') continue;

    const legality = getCardLegality(card.card, format);

    if (legality.isBanned) {
      const isOverridden = allowedSet.has(card.scryfall_id);
      issues.push({
        type: 'card_banned',
        severity: 'error',
        message: `${card.card.name} is banned in ${format}`,
        card_id: card.scryfall_id,
        card_name: card.card.name,
        suggested_action: 'Remove card',
        can_override: true,
        is_overridden: isOverridden,
      });
    } else if (!legality.isLegal) {
      const isOverridden = allowedSet.has(card.scryfall_id);
      issues.push({
        type: 'card_not_legal',
        severity: 'error',
        message: `${card.card.name} is not legal in ${format}`,
        card_id: card.scryfall_id,
        card_name: card.card.name,
        suggested_action: 'Remove card or change format',
        can_override: true,
        is_overridden: isOverridden,
      });
    } else if (legality.isRestricted && card.quantity > 1) {
      const isOverridden = allowedSet.has(card.scryfall_id);
      issues.push({
        type: 'card_restricted',
        severity: 'error',
        message: `${card.card.name} is restricted to 1 copy in ${format}`,
        card_id: card.scryfall_id,
        card_name: card.card.name,
        suggested_action: 'Reduce to 1 copy',
        can_override: true,
        is_overridden: isOverridden,
      });
    }
  }

  return issues;
}

function validateRarityRestriction(cards: EnrichedDeckCard[], rarity: string, allowedSet: Set<string>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  for (const card of cards) {
    if (card.zone === 'maybeboard' || card.zone === 'tokens') continue;

    // For pauper, card must have been printed at common at some point
    // This is simplified - full pauper legality requires checking all printings
    const cardRarity = card.card.rarity;

    if (rarity === 'common' && cardRarity !== 'common') {
      // In reality, we'd need to check if ANY printing was common
      // For now, just check the specific card's rarity
      const isOverridden = allowedSet.has(card.scryfall_id);
      issues.push({
        type: 'pauper_rarity',
        severity: 'error',
        message: `${card.card.name} is ${cardRarity} but Pauper requires commons`,
        card_id: card.scryfall_id,
        card_name: card.card.name,
        suggested_action: 'Find a common printing or remove card',
        can_override: true,
        is_overridden: isOverridden,
      });
    }
  }

  return issues;
}

function generateWarnings(cards: EnrichedDeckCard[], format?: DeckFormat | null): ValidationIssue[] {
  const warnings: ValidationIssue[] = [];

  // Count cards by zone
  const mainZones: DeckZone[] = ['commander', 'companion', 'main'];
  const mainCards = cards.filter((c) => mainZones.includes(c.zone));
  const mainCount = mainCards.reduce((sum, c) => sum + c.quantity, 0);

  // Check if limited format
  const rules = format ? FORMAT_RULES[format] : null;
  const isLimited = rules?.is_limited === true;

  // Warning: Limited deck over 41 cards
  if (isLimited && mainCount > 41) {
    warnings.push({
      type: 'limited_deck_size',
      severity: 'warning',
      message: `Limited deck has ${mainCount} cards. Most limited decks run exactly 40 cards for consistency.`,
      suggested_action: 'Consider cutting to 40 cards for better consistency',
      can_override: false,
      is_overridden: false,
    });
  }

  // Warning: No lands
  const landCount = mainCards.filter((c) => c.classification.is_land).reduce((sum, c) => sum + c.quantity, 0);

  if (mainCards.length > 0 && landCount === 0) {
    warnings.push({
      type: 'no_lands',
      severity: 'warning',
      message: 'Deck has no lands',
      suggested_action: 'Consider adding lands for mana',
      can_override: false,
      is_overridden: false,
    });
  }

  return warnings;
}

// ============================================================================
// Utility Functions
// ============================================================================

// Re-export isBasicLand from deckUtils for consumers
export { isBasicLand } from '@/lib/deckUtils';

/**
 * Check if a card can have unlimited copies in a deck.
 * True for basic lands and cards with "A deck can have any number of cards named" text.
 */
export function canHaveUnlimitedCopies(card: {
  name: string;
  oracle_text?: string | null;
  type_line?: string | null;
  layout?: string | null;
}): boolean {
  // Basic lands
  if (isBasicLand(card.name)) return true;

  // Tokens and emblems have no copy limits
  if (isTokenOrEmblem(card)) return true;

  // Cards with "A deck can have any number of cards named" (Relentless Rats, Shadowborn Apostle, etc.)
  if (card.oracle_text?.includes('A deck can have any number of cards named')) return true;

  return false;
}

/**
 * Get the maximum number of copies allowed for a format.
 */
export function getMaxCopiesForFormat(format: DeckFormat | null): number {
  if (!format) return 999;
  return FORMAT_RULES[format]?.max_copies ?? 4;
}

/**
 * Check if a card can be used as a commander.
 * Valid commanders are:
 * - Legendary creatures
 * - Cards that explicitly say "can be your commander"
 * - For Commander: legendary Background enchantments (as a second commander)
 * - For Oathbreaker: also planeswalkers
 * - For Brawl: legendary planeswalkers
 */
export function canBeCommander(card: { type_line?: string | null; oracle_text?: string | null }, format: DeckFormat | null): boolean {
  const typeLine = (card.type_line ?? '').toLowerCase();
  const oracleText = (card.oracle_text ?? '').toLowerCase();

  // Check for "can be your commander" text
  if (oracleText.includes('can be your commander')) return true;

  // Legendary creature
  if (typeLine.includes('legendary') && typeLine.includes('creature')) return true;

  // Backgrounds pair with a "Choose a Background" commander (EDH only)
  if (format === 'commander' && isBackgroundCard(card)) return true;

  // For Oathbreaker: planeswalkers can be commanders
  if (format === 'oathbreaker' && typeLine.includes('planeswalker')) return true;

  // For Brawl: also legendary planeswalkers
  if (format === 'brawl' && typeLine.includes('legendary') && typeLine.includes('planeswalker')) return true;

  return false;
}

/**
 * Get card legality for a specific format.
 * Uses Scryfall's legalities field.
 */
function getCardLegality(card: ScryfallCard, format: DeckFormat): CardLegalityInfo {
  const legalities = card.legalities;
  if (!legalities) {
    return { isLegal: true, isBanned: false, isRestricted: false };
  }

  const formatKey = format as keyof typeof legalities;
  const status = legalities[formatKey];

  return {
    isLegal: status === 'legal' || status === 'restricted',
    isBanned: status === 'banned',
    isRestricted: status === 'restricted',
    reason: status === 'not_legal' ? 'Not printed in format' : undefined,
  };
}

/**
 * Calculate the color identity of a deck based on its cards.
 */
export function calculateColorIdentity(cards: EnrichedDeckCard[]): Color[] {
  const colors = new Set<Color>();

  // For commander formats, identity is from commanders only
  const commanders = cards.filter((c) => c.zone === 'commander');
  if (commanders.length > 0) {
    for (const commander of commanders) {
      const identity = commander.card.color_identity ?? [];
      identity.forEach((c) => colors.add(c));
    }
  } else {
    // For non-commander formats, include all cards
    for (const card of cards) {
      if (card.zone === 'maybeboard' || card.zone === 'tokens') continue;
      const identity = card.card.color_identity ?? [];
      identity.forEach((c) => colors.add(c));
    }
  }

  // Return in WUBRG order
  const order: Color[] = ['W', 'U', 'B', 'R', 'G'];
  return order.filter((c) => colors.has(c));
}

/**
 * Get a summary of validation issues for display.
 */
export function getValidationSummary(validation: DeckValidation): string {
  const parts: string[] = [];

  const unresolvedErrors = validation.errors.filter((e) => !e.is_overridden);
  if (unresolvedErrors.length > 0) {
    parts.push(`${unresolvedErrors.length} error${unresolvedErrors.length === 1 ? '' : 's'}`);
  }

  if (validation.has_overrides) {
    const overrideCount = validation.errors.filter((e) => e.is_overridden).length;
    parts.push(`${overrideCount} override${overrideCount === 1 ? '' : 's'}`);
  }

  if (validation.warnings.length > 0) {
    parts.push(`${validation.warnings.length} warning${validation.warnings.length === 1 ? '' : 's'}`);
  }

  if (parts.length === 0) {
    return validation.is_valid ? 'Valid' : 'Invalid';
  }

  return parts.join(', ');
}

// ============================================================================
// Token/Emblem Detection
// ============================================================================

type TokenCheckInput = { type_line?: string | null; layout?: string | null };

// Some token-layout cards (e.g. The Monarch, The Initiative) have type_line "Card".
const TOKEN_LAYOUTS: ReadonlySet<string> = new Set(['token', 'double_faced_token', 'emblem']);

/**
 * Check if a card is a token or emblem based on its layout or type line.
 * These should go to the 'tokens' zone and not count toward deck limits.
 */
export function isTokenOrEmblem(card: TokenCheckInput | null | undefined): boolean {
  if (!card) return false;
  if (card.layout && TOKEN_LAYOUTS.has(card.layout)) return true;
  const lower = (card.type_line ?? '').toLowerCase();
  return lower.includes('token') || lower.includes('emblem');
}

/**
 * Get the default zone for a card based on its type.
 * Tokens and emblems go to 'tokens' zone, everything else to 'main'.
 */
export function getDefaultZoneForCard(card: TokenCheckInput | null | undefined): 'main' | 'tokens' {
  return isTokenOrEmblem(card) ? 'tokens' : 'main';
}

// ============================================================================
// Zone Rules & Restrictions
// ============================================================================

/** Minimal card shape for zone rule checks */
type ZoneCardInput = { type_line?: string | null; oracle_text?: string | null; layout?: string | null };

/**
 * Zone restriction rules defining what cards can go where.
 */
export interface ZoneRules {
  /** Check if a card type can be placed in this zone */
  canAcceptCard: (card: ZoneCardInput) => boolean;
  /** Check if a card type MUST be in this zone */
  requiresZone: (card: ZoneCardInput) => boolean;
  /** Check if zone is available for this deck (format/status) */
  isAvailable: (format: DeckFormat | null, status: DeckStatus) => boolean;
}

/**
 * Check if a card has the Companion keyword ability.
 */
export function hasCompanionAbility(card: ZoneCardInput): boolean {
  const oracleText = (card.oracle_text ?? '').toLowerCase();
  return oracleText.includes('companion —') || oracleText.includes('companion—');
}

/**
 * Zone rules defining what cards can be placed in each zone.
 */
export const ZONE_RULES: Record<DeckZone, ZoneRules> = {
  commander: {
    canAcceptCard: (card) => canBeCommander(card, 'commander'),
    requiresZone: () => false,
    isAvailable: (format) => isCommanderFormat(format),
  },
  companion: {
    canAcceptCard: (card) => hasCompanionAbility(card),
    requiresZone: () => false,
    isAvailable: () => true,
  },
  main: {
    canAcceptCard: (card) => !isTokenOrEmblem(card),
    requiresZone: () => false,
    isAvailable: () => true,
  },
  sideboard: {
    canAcceptCard: (card) => !isTokenOrEmblem(card),
    requiresZone: () => false,
    isAvailable: (format) => (format ? FORMAT_RULES[format]?.has_sideboard !== false : true),
  },
  maybeboard: {
    canAcceptCard: (card) => !isTokenOrEmblem(card),
    requiresZone: () => false,
    isAvailable: () => true,
  },
  tokens: {
    canAcceptCard: (card) => isTokenOrEmblem(card),
    requiresZone: (card) => isTokenOrEmblem(card),
    isAvailable: () => true, // Always available, but only shown when cards exist
  },
};

/** User-selectable zones (excludes auto-routed commander/companion/tokens). */
const SELECTABLE_ZONES: DeckZone[] = ['main', 'sideboard', 'maybeboard'];

/**
 * Get the user-selectable zones available for a deck's format and status.
 * Returns a subset of [main, sideboard, maybeboard] — excludes zones that are
 * auto-routed (commander, companion, tokens).
 */
export function getSelectableZones(format: DeckFormat | null, status: DeckStatus): DeckZone[] {
  return SELECTABLE_ZONES.filter((zone) => ZONE_RULES[zone].isAvailable(format, status));
}

/**
 * Get available zones for a specific card based on its type, format, and deck status.
 */
export function getAvailableZonesForCard(card: ZoneCardInput, format: DeckFormat | null, status: DeckStatus): DeckZone[] {
  return (Object.entries(ZONE_RULES) as [DeckZone, ZoneRules][])
    .filter(([_, rules]) => rules.isAvailable(format, status) && rules.canAcceptCard(card))
    .map(([zone]) => zone);
}

/**
 * Get the required zone for a card (null if no specific requirement).
 * Currently only tokens/emblems are required to be in the tokens zone.
 */
export function getRequiredZoneForCard(card: ZoneCardInput): DeckZone | null {
  for (const [zone, rules] of Object.entries(ZONE_RULES) as [DeckZone, ZoneRules][]) {
    if (rules.requiresZone(card)) return zone;
  }
  return null;
}

/**
 * Check if a card can be moved to a specific zone.
 * Returns allowed status and reason if not allowed.
 */
export function canMoveToZone(
  card: ZoneCardInput,
  targetZone: DeckZone,
  format: DeckFormat | null,
  status: DeckStatus,
): { allowed: boolean; reason?: string } {
  const rules = ZONE_RULES[targetZone];

  if (!rules.isAvailable(format, status)) {
    return { allowed: false, reason: `${DECK_ZONE_LABELS[targetZone]} is not available for this deck` };
  }

  if (!rules.canAcceptCard(card)) {
    if (targetZone === 'tokens') {
      return { allowed: false, reason: 'Only token and emblem cards can be in the Tokens zone' };
    }
    if (isTokenOrEmblem(card)) {
      return { allowed: false, reason: 'Token cards must be in the Tokens zone' };
    }
    return { allowed: false, reason: `This card cannot be placed in ${DECK_ZONE_LABELS[targetZone]}` };
  }

  return { allowed: true };
}

/**
 * Validate zone assignments for all cards in a deck.
 * Checks that tokens are in tokens zone and non-tokens are not.
 */
function validateZoneAssignments(cards: EnrichedDeckCard[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  for (const card of cards) {
    const requiredZone = getRequiredZoneForCard(card.card);
    if (requiredZone && card.zone !== requiredZone) {
      issues.push({
        type: 'zone_invalid',
        severity: 'error',
        message: `${card.card.name} must be in ${DECK_ZONE_LABELS[requiredZone]}`,
        card_id: card.scryfall_id,
        card_name: card.card.name,
        suggested_action: `Move to ${DECK_ZONE_LABELS[requiredZone]}`,
        can_override: false,
        is_overridden: false,
      });
    }
  }

  return issues;
}
